import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import styled from 'styled-components'
import { useStateValue } from 'providers'
import { useGetQuery, usePostQuery } from 'hooks'
import { apiFetch } from 'actions/apiFetch'
import { Button, Card, FormInput, Icon, Link, Modal, PaginateTable, TextInput, Title } from 'components'
import { getRelativeTime, getUserName } from 'utils'
import { RequireUsername } from '../games/chat/components/require-username'

const Suggestions = styled.div`
  ${({ theme: { colors } }) => `
  border: 1px solid ${colors('foreground', 0.15)};
  border-top: none;
  margin-bottom: 1rem;
  `}
`

const Suggestion = styled.div`
  ${({ theme: { colors } }) => `
  padding: 0.5rem 0.75rem;
  cursor: pointer;
  &:hover {
    background: ${colors('chrome')};
  }
  `}
`

const SelectedUser = styled.div`
  ${({ theme: { colors } }) => `
  padding: 0.5rem 0.75rem;
  margin-bottom: 1rem;
  background: ${colors('link', 0.12)};
  border: 1px solid ${colors('link', 0.35)};
  `}
`

export const MessagesList = () => {
  const [
    {
      session: { user }
    }
  ] = useStateValue()
  const navigate = useNavigate()
  const [page, setPage] = useState({ page: 1, limit: 10 })
  const [showModal, setShowModal] = useState(false)
  const [query, setQuery] = useState('')
  const [suggestions, setSuggestions] = useState([])
  const [selectedUser, setSelectedUser] = useState(null)
  const [body, setBody] = useState('')

  const { data, reFetch } = useGetQuery({
    url: `/messages?limit=${page.limit}&offset=${page.limit * (page.page - 1)}`
  })
  const { post: createMessage } = usePostQuery({ url: '/messages' })

  useEffect(() => {
    if (selectedUser || query.trim().length < 2) {
      setSuggestions([])
      return
    }
    const timeout = setTimeout(() => {
      apiFetch({ path: `/users/search?q=${encodeURIComponent(query)}` })
        .then((res) => setSuggestions(res?.users || []))
        .catch(() => setSuggestions([]))
    }, 300)
    return () => clearTimeout(timeout)
  }, [query, selectedUser])

  const closeModal = () => {
    setShowModal(false)
    setQuery('')
    setSuggestions([])
    setSelectedUser(null)
    setBody('')
  }

  const pickUser = (pickedUser) => {
    setSelectedUser(pickedUser)
    setQuery(getUserName(pickedUser))
    setSuggestions([])
  }

  const submit = async () => {
    if (!selectedUser || body.trim() === '') return
    await createMessage({ to_username: selectedUser.username, body })
    const username = selectedUser.username
    closeModal()
    reFetch(true)
    navigate(`/messages/${username}`)
  }

  const rows = data?.threads?.rows || []

  return (
    <Card>
      <Title level={2} color="link">
        messages
      </Title>
      <RequireUsername user={user}>
        <PaginateTable
          data={rows}
          count={data?.threads?.count || 0}
          addItem={() => setShowModal(true)}
          onRowClick={(row) => navigate(`/messages/${row.username}`)}
          columns={[
            { title: 'From', key: 'username', render: (value, index, row) => getUserName(row) },
            { title: 'Last message', key: 'last_body' },
            {
              title: 'When',
              key: 'last_created_at',
              className: 'hide-sm',
              render: (value) => getRelativeTime(value)
            },
            {
              title: '',
              key: 'unread_count',
              render: (value) => (value > 0 ? `(${value})` : null)
            },
            {
              title: 'Actions',
              key: 'id',
              className: 'right',
              render: (value, index, row) => (
                <Link to={`/messages/${row.username}`} onClick={(e) => e.stopPropagation()}>
                  <Icon icon="eye" />
                </Link>
              )
            }
          ]}
          onPageChange={setPage}
        />
        <Modal show={showModal} hideModal={closeModal} title="New Message">
          {selectedUser
            ? (
              <SelectedUser>{getUserName(selectedUser)}</SelectedUser>
              )
            : (
              <FormInput
                label="To"
                name="username"
                placeholder="username"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
              />
              )}
          {suggestions.length > 0 && (
            <Suggestions>
              {suggestions.map((suggestedUser) => (
                <Suggestion key={suggestedUser.id} onClick={() => pickUser(suggestedUser)}>
                  {getUserName(suggestedUser)}
                </Suggestion>
              ))}
            </Suggestions>
          )}
          <TextInput name="body" value={body} onChange={(e) => setBody(e.target.value)} />
          <Button onPress={submit}>Send</Button>
        </Modal>
      </RequireUsername>
    </Card>
  )
}
